import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { LF_ID_RX } from "../components/LostFoundId";
import { LfIdLookup } from "../components/LfIdLookup";
import { Pagination } from "../components/Pagination";
import { api } from "../lib/api";
import { CATEGORIES, CATEGORY_LABEL } from "../lib/constants";
import { BannerAd } from "../components/BannerAd";
import { CategoryBanner } from "../components/CategoryBanner";
import { FeaturedCarousel } from "../components/FeaturedCarousel";
import { LostFoundHeader, LostFoundTabs } from "../components/LostFoundHeader";
import { CategoryTabs, CategoryStrip } from "../components/browse/CategoryTabs";
import { FilterSidebar } from "../components/browse/FilterSidebar";
import { ResultsToolbar, DidYouMean } from "../components/browse/ResultsToolbar";
import { ResultsView } from "../components/browse/ResultsView";
import { useBrowseListings } from "../hooks/useBrowseListings";
import { useLanguage } from "../context/LanguageContext";
import { catLabel } from "../lib/i18n";
import { HomePopup } from "../components/HomePopup";
import { RecentlyViewed } from "../components/browse/RecentlyViewed";
import { SimilarListings } from "../components/browse/SimilarListings";
import { FreshMore } from "../components/FreshMore";
import { useAppendPages } from "../hooks/useAppendPages";
import { useHomeFeed } from "../lib/siteContent";

export default function Browse() {
  const { t, lang } = useLanguage();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState("grid");
  const [catTheme, setCatTheme] = useState({ overrides: {}, on: false });

  const category = params.get("category") || "all";
  const q = params.get("q") || "";
  const isLF = category === "lost-found";
  const lf = isLF ? (params.get("lf") || "all") : "all";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const feed = useHomeFeed();
  const append = feed.browse_mode === "button" || feed.browse_mode === "scroll";
  const res = useBrowseListings(params, append ? 1 : page);
  const more = useAppendPages(params, res, append);
  const navigate = useNavigate();
  useEffect(() => {
    if (LF_ID_RX.test(q) && !res.loading && res.total === 1 && res.items[0]) navigate(`/listing/${res.items[0].id}`, { replace: true });
  }, [q, res.loading, res.total, res.items, navigate]);
  const sort = params.get("sort") || (q ? "relevance" : "newest");

  useEffect(() => {
    api.get("/site/categories").then((r) => setCatTheme({ overrides: r.data.overrides || {}, on: !!r.data.browse_colors })).catch(() => {});
  }, []);
  const colorFor = useCallback((id) => (catTheme.on ? catTheme.overrides[id]?.color || "" : ""), [catTheme]);

  const setParam = useCallback((k, v) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      v && v !== "all" ? next.set(k, v) : next.delete(k);
      if (k === "category" && v !== "lost-found") next.delete("lf");
      if (k === "q") next.delete("sort");
      next.delete("page");
      return next;
    });
  }, [setParams]);
  const pickCategory = useCallback((id) => setParam("category", id), [setParam]);
  const clearAll = () => setParams(new URLSearchParams());
  const goPage = (p) => {
    const next = new URLSearchParams(params);
    p > 1 ? next.set("page", String(p)) : next.delete("page");
    setParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveFilters = useMemo(() => ({
    category, province: params.get("province") || "all", city: params.get("city") || "", q,
    verified: params.get("verified") || "all", lf_type: lf === "reunited" ? "" : lf,
    min_price: params.get("min_price") || "", max_price: params.get("max_price") || "",
  }), [category, params, q, lf]);
  const catName = catLabel(lang, category, CATEGORY_LABEL[category] || "");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <HomePopup page={isLF ? "lostfound" : "browse"} />
      <CategoryTabs category={category} lang={lang} colorFor={colorFor} onPick={pickCategory} />
      {!isLF && category !== "all" && colorFor(category) && (
        <CategoryStrip color={colorFor(category)} icon={CATEGORIES.find((c) => c.id === category)?.icon}
          label={catTheme.overrides[category]?.label || catName}
          description={catTheme.overrides[category]?.description} total={res.total} />
      )}
      {isLF && <LostFoundHeader />}
      {isLF && <LostFoundTabs value={lf} onChange={(v) => setParam("lf", v)} />}
      {isLF && <LfIdLookup />}
      {category !== "all" && <FeaturedCarousel category={category} embedded title={isLF ? t("lf_featured") : `Featured in ${catName}`} />}

      <div className="grid lg:grid-cols-12 gap-8 mt-6">
        <FilterSidebar t={t} params={params} setParams={setParams} setParam={setParam} isLF={isLF} onClear={clearAll} />
        <main className="lg:col-span-9">
          <BannerAd variant="strip" className="mb-5" />
          <ResultsToolbar t={t} params={params} total={res.total} sort={sort} hasQuery={!!q} loading={res.loading && !res.firstLoad}
            view={view} setView={setView} setParam={setParam} saveFilters={saveFilters} />
          {res.didYouMean && !res.loading && <DidYouMean t={t} word={res.didYouMean} onPick={(w) => setParam("q", w)} />}
          <ResultsView t={t} listings={append ? more.items : res.items} firstLoad={res.firstLoad} loading={res.loading} view={view}
            province={params.get("province") || "all"} onClear={clearAll} />
          {!res.firstLoad && append && (
            <>
              <FreshMore mode={feed.browse_mode} hasMore={more.hasMore} count={more.items.length} onMore={more.loadMore} t={t}
                cap={120} afterCap="button" loading={more.loading} testid="browse" />
              {more.items.length > 0 && <p className="text-center text-xs text-slate-400 mt-3" data-testid="browse-shown-info">{lang === "fr" ? `${more.items.length} sur ${res.total} annonces` : `Showing ${more.items.length} of ${res.total} listings`}</p>}
            </>
          )}
          {!res.firstLoad && !append && <Pagination page={page} pages={res.pages} onChange={goPage} prevLabel={lang === "fr" ? "Préc." : "Prev"} nextLabel={lang === "fr" ? "Suiv." : "Next"} />}
          {!res.firstLoad && !append && res.pages > 1 && <p className="text-center text-xs text-slate-400 mt-3" data-testid="pagination-info">{lang === "fr" ? `Page ${page} sur ${res.pages}` : `Page ${page} of ${res.pages}`}</p>}
          {feed.similar_row !== false && <SimilarListings t={t} lang={lang} className="mt-10" />}
          {feed.recent_row !== false && <RecentlyViewed t={t} lang={lang} className="mt-10" />}
          <BannerAd variant="strip_bottom" className="mt-8" />
        </main>
      </div>
      {!isLF && <CategoryBanner category={category} />}
    </div>
  );
}
